import i18n from '../i18n';

type ShareMailTemplateParams = {
  fundraiserName?: string;
  projectName?: string;
  projectUrl: string;
  defaultProjectName?: string;
};

const sanitizeMailText = (value?: string) =>
  String(value ?? '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\*/g, '')
    .trim();

const normalizeFundraiserName = (name?: string) => {
  const trimmed = sanitizeMailText(name);
  if (/^\{[^}]+\}$/.test(trimmed)) return i18n.t('common.share.fundraiserFallback');
  return trimmed || i18n.t('common.share.fundraiserFallback');
};

const normalizeProjectName = (name?: string, fallback?: string) => {
  const trimmed = sanitizeMailText(name);
  if (/^\{[^}]+\}$/.test(trimmed)) {
    const fallbackTrimmed = sanitizeMailText(fallback);
    return fallbackTrimmed || i18n.t('common.shareModal.defaultTitle');
  }
  if (trimmed) return trimmed;
  const fallbackTrimmed = sanitizeMailText(fallback);
  return fallbackTrimmed || i18n.t('common.shareModal.defaultTitle');
};

const MAIL_SIGNATURE_LINES = [
  'www.missionfund.org',
  '',
  i18n.t('common.mail.signatureKo'),
  i18n.t('common.mail.signatureEn'),
];

export const buildProjectShareMailTemplate = ({
  fundraiserName,
  projectName,
  projectUrl,
  defaultProjectName,
}: ShareMailTemplateParams) => {
  const resolvedFundraiserName = normalizeFundraiserName(fundraiserName);
  const resolvedProjectName = normalizeProjectName(projectName, defaultProjectName);
  const subject = i18n.t('common.shareMail.project.subject', { fundraiser: resolvedFundraiserName, project: resolvedProjectName });
  const body = [
    i18n.t('common.shareMail.project.started'),
    '',
    i18n.t('common.shareMail.project.card'),
    i18n.t('common.shareMail.project.projectName', { project: resolvedProjectName }),
    i18n.t('common.shareMail.project.fundraiser', { fundraiser: resolvedFundraiserName }),
    '',
    i18n.t('common.shareMail.project.body'),
    i18n.t('common.shareMail.project.greeting'),
    i18n.t('common.shareMail.project.intro', { fundraiser: resolvedFundraiserName, project: resolvedProjectName }),
    '',
    i18n.t('common.shareMail.project.encourage'),
    i18n.t('common.shareMail.project.request'),
    '',
    i18n.t('common.shareMail.project.cta'),
    projectUrl,
    '',
    ...MAIL_SIGNATURE_LINES,
  ].join('\n');

  return { subject, body };
};

export const buildPostShareMailTemplate = ({
  fundraiserName,
  projectName,
  projectUrl,
  defaultProjectName,
}: ShareMailTemplateParams) => {
  const resolvedFundraiserName = normalizeFundraiserName(fundraiserName);
  const resolvedProjectName = normalizeProjectName(projectName, defaultProjectName);
  const subject = i18n.t('common.shareMail.post.subject', { fundraiser: resolvedFundraiserName, project: resolvedProjectName });
  const body = [
    i18n.t('common.shareMail.post.title', { project: resolvedProjectName }),
    '',
    i18n.t('common.shareMail.post.card'),
    i18n.t('common.shareMail.post.projectName', { project: resolvedProjectName }),
    '',
    i18n.t('common.shareMail.post.fundraiser', { fundraiser: resolvedFundraiserName }),
    '',
    i18n.t('common.shareMail.post.body'),
    i18n.t('common.shareMail.post.greeting'),
    i18n.t('common.shareMail.post.intro', { fundraiser: resolvedFundraiserName, project: resolvedProjectName }),
    '',
    i18n.t('common.shareMail.post.thanks'),
    '',
    projectUrl,
    '',
    ...MAIL_SIGNATURE_LINES,
  ].join('\n');

  return { subject, body };
};

export const buildSupporterMessageMailTemplate = ({
  fundraiserName,
  projectUrl,
}: ShareMailTemplateParams) => {
  const resolvedFundraiserName = normalizeFundraiserName(fundraiserName);
  const resolvedProjectUrl = sanitizeMailText(projectUrl) || 'https://go.missionfund.org/';
  const subject = i18n.t('common.shareMail.supporter.subject', { fundraiser: resolvedFundraiserName });
  const body = [
    i18n.t('common.shareMail.supporter.greeting'),
    '',
    i18n.t('common.shareMail.supporter.title', { fundraiser: resolvedFundraiserName }),
    '',
    i18n.t('common.shareMail.supporter.brand'),
    resolvedProjectUrl,
  ].join('\n');

  return { subject, body };
};
